import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectOrgs } from "../../../../store/userDataSlice";
import { setOrg } from "../../../../store/contextSlice";
import { RightOutlined, CheckOutlined } from "@ant-design/icons";
import { Input, Tag } from "antd";
import { Org } from "../../../../schema/types/org/entity";

export const GoToConsoleSection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const orgs = useAppSelector(selectOrgs);

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [orgSearch, setOrgSearch] = React.useState("");

  const filteredOrgs = React.useMemo(() => {
    const activeOrgs: Org[] = [];
    const inactiveOrgs: Org[] = [];

    for (const org of orgs) {
      if (orgSearch && !org.name.toLowerCase().includes(orgSearch.toLowerCase())) {
        continue;
      }
      if (!org.status?.active) {
        inactiveOrgs.push(org);
      } else {
        activeOrgs.push(org);
      }
    }

    return [...activeOrgs, ...inactiveOrgs];
  }, [orgs, orgSearch]);

  function handleSelectOrg(orgName: string) {
    dispatch(setOrg(orgName));
    navigate(`/console/org/${orgName}`);
  }

  return (
    <div className="go-to-console-section">
      <button
        className="go-to-console-button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="go-to-console-label">Go to Console</span>
        <RightOutlined
          className="go-to-console-chevron"
          style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
        />
      </button>

      {isExpanded && (
        <div className="go-to-console-content">
          <div className="go-to-console-description">Select an org to continue</div>

          {orgs.length > 5 && (
            <Input
              size="small"
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              placeholder="Filter orgs..."
              allowClear
              className="go-to-console-filter"
            />
          )}

          <div className="go-to-console-org-list">
            {filteredOrgs.map((org) => (
              <OrgCard key={org.name} org={org} onSelect={handleSelectOrg} />
            ))}
          </div>
        </div>
      )}

      <div className="menu-list-divider" />
    </div>
  );
};

interface OrgCardProps {
  org: Org;
  onSelect: (orgName: string) => void;
}

const OrgCard: React.FC<OrgCardProps> = ({ org, onSelect }) => {
  const isInactive = !org.status?.active;

  return (
    <button
      className={`go-to-console-org-card ${isInactive ? "inactive" : ""}`}
      onClick={() => onSelect(org.name)}
    >
      <div className="go-to-console-org-card-content">
        <div className="go-to-console-org-name">{org.name}</div>
        {org.description && org.description !== org.name && (
          <div className="go-to-console-org-description">{org.description}</div>
        )}
      </div>
      {isInactive && <Tag color="error">Inactive</Tag>}
    </button>
  );
};
